import { Component, EventEmitter, Input, Output } from '@angular/core';
import { boxToPx, HighlightBox, PxBox } from '../document-highlight-layer/document-highlight-layer.component';

/**
 * A single hoverable line region on the document: a normalized box plus the opaque key of the list
 * row it maps back to. Structurally compatible with the quotations `DocumentLineRegion`.
 */
export interface HotspotRegion {
  key: string;
  box: HighlightBox;
  page?: number;
}

/**
 * Interactive sibling of `document-highlight-layer` (WB-4974, FE-7 — bidirectional hover).
 * Renders one transparent, `pointer-events:auto` strip per line region over the rendered page/image;
 * hovering a strip emits the line key so the host can highlight the matching list row. The root stays
 * `pointer-events:none` so empty document areas remain scrollable / selectable — only the strips catch
 * the pointer. The host positions this layer to exactly cover the target element (same inputs as the
 * highlight layer).
 */
@Component({
  selector: 'app-document-hotspot-layer',
  templateUrl: './document-hotspot-layer.component.html',
  styleUrls: ['./document-hotspot-layer.component.scss'],
})
export class DocumentHotspotLayerComponent {
  @Input() regions: HotspotRegion[] = [];
  /** Rendered size (px) of the target element this layer covers. */
  @Input() width = 0;
  @Input() height = 0;
  /** Position (px) of the target element relative to the positioned host. */
  @Input() offsetLeft = 0;
  @Input() offsetTop = 0;

  /** Emits the hovered region's key, or null on leave. */
  @Output() hotspotHover = new EventEmitter<string | null>();

  get pxRegions(): Array<PxBox & { key: string }> {
    if (!this.width || !this.height) {
      return [];
    }
    return (this.regions || []).map((r) => ({ ...boxToPx(r.box, this.width, this.height), key: r.key }));
  }

  trackByKey(_index: number, region: { key: string }): string {
    return region.key;
  }
}
